import { Store } from '@reduxjs/toolkit';
import { path, pick } from 'ramda';

import { selectCart } from '../cart/selectors';
import { selectDashboard } from '../dashboard/selectors';
import {
  selectCfaId,
  selectGuestDetailsValid,
  selectGuestFormSyncErrors,
  selectValidEmail,
  selectValidPhone,
} from '../guest/selectors';
import { selectMenu } from '../menu/reducer';
import {
  selectDetailsDate,
  selectDetailsFormSyncErrors,
  selectDetailsFormValues,
  selectDetailsTime,
  selectNewPaymentMethod,
} from '../order/selectors';
import {
  selectEventDetailsValid,
  selectFormSubmitWarning,
  selectFormSyncErrors,
  selectPaymentSubmitWarning,
  selectPaymentValid,
  selectPayWithVaultedCard,
} from '../payment/selectors';
import { State as RootState } from '../reducers';
import { selectDateIsToday, selectTimeShouldClear } from '../shared/selectors';
import {
  selectLocationAcceptsDelivery,
  selectLocationAcceptsPickup,
  selectLocationContactDetails,
} from '../user/selectors';
import { maskEmail } from './utils';

const UNAVAILABLE = 'n/a';

export function getUserData(state: RootState) {
  const { user } = state;
  const userData = pick(
    [
      'selectedLocation',
      'error',
      'loading',
      'bypassBusinessRules',
      'cfaAud',
      'cfaGuid',
      'permissions',
      'email',
      'cmtEula',
    ],
    user,
  );

  return {
    ...userData,
    email: maskEmail(userData.email),
    locationContactDetails: selectLocationContactDetails(state) ?? UNAVAILABLE,
    locationAcceptsDelivery: selectLocationAcceptsDelivery(state) ?? UNAVAILABLE,
    locationAcceptsPickup: selectLocationAcceptsPickup(state) ?? UNAVAILABLE,
  };
}

function getOrderData(state: RootState) {
  const { order } = state;
  const orderData = pick(
    [
      'id',
      'editOrderId',
      'reorder',
      'error',
      'loading',
      'status',
      'orderType',
      'reorder',
      'destination',
      'taxAmount',
      'subTotalAmount',
      'promiseDateTime',
      'companyName',
    ],
    order,
  );
  return orderData;
}

function getGuestData(state: RootState) {
  const { guest } = state;
  const selectedCard = typeof guest.selectedCard === 'object' ? guest.selectedCard : undefined;

  const guestData = {
    cfaId: selectCfaId(state),
    email: maskEmail(path(['cmtCustomer', 'email'], guest)) ?? UNAVAILABLE,
    error: guest.error,
    vaultedCardsCount: Array.isArray(guest.vaultedCards) ? guest.vaultedCards.length : undefined,
    selectedCard: {
      type: selectedCard?.type,
      cardTtype: selectedCard?.cardType,
      account: selectedCard?.accountDisplay,
      validated: selectedCard?.validated,
      countryCode: selectedCard?.countryCode,
    },
    cmtCustomer: path(['cmtCustomer'], guest),
  };
  return guestData;
}

function getCartData(state: RootState) {
  return selectCart(state);
}

function getDashboardData(state: RootState) {
  const dashboard = selectDashboard(state);
  return {
    loading: dashboard.loading,
    error: dashboard.error,
  };
}

function getMenuData(state: RootState) {
  const menu = selectMenu(state);
  return {
    loading: menu.loading,
    error: menu.error,
  };
}

const getFormData = (state: RootState) => {
  try {
    return {
      cfaId: selectCfaId(state),
      general: {
        formSyncErrors: selectFormSyncErrors(state),
        formSubmitWarning: selectFormSubmitWarning(state),
      },
      eventDetails: {
        eventDetailsValid: selectEventDetailsValid(state),
        detailsFormValues: selectDetailsFormValues(state),
        timeShouldClear: selectTimeShouldClear(state),
        dateIsToday: selectDateIsToday(state),
        date: selectDetailsDate(state),
        time: selectDetailsTime(state),
        detailsFormSyncErrors: selectDetailsFormSyncErrors(state),
      },
      guestDetails: {
        guestDetailsValid: selectGuestDetailsValid(state),
        validEmail: selectValidEmail(state),
        validPhone: selectValidPhone(state),
        guestFormSyncErrors: selectGuestFormSyncErrors(state),
      },
      payment: {
        paymentSubmitWarning: selectPaymentSubmitWarning(state),
        payWithVaultedCard: selectPayWithVaultedCard(state),
        newPaymentMethod: selectNewPaymentMethod(state),
        paymentValid: selectPaymentValid(state),
        selectedPaymentMethod: state.order?.selectedPaymentMethod,
      },
    };
  } catch (error) {
    return {
      message: 'Unable to get form data',
      error,
    };
  }
};

export default function getBugSnagStateData(getState: Store['getState']) {
  try {
    const state = getState();
    return {
      guest: getGuestData(state),
      user: getUserData(state),
      cart: getCartData(state),
      order: getOrderData(state),
      menu: getMenuData(state),
      dashboard: getDashboardData(state),
      formData: getFormData(state),
    };
  } catch (error) {
    return {
      message: 'Unable to get session metadata',
      error,
    };
  }
}
